import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ModalComponent, ModalConfig } from './modal';

export interface ModalOptions extends ModalConfig {
  headerContent?: string;
  bodyContent?: string;
  footerContent?: string;
  width?: string;
  footerButtons?: Array<{
    label: string;
    cssClass?: string;
    action?: () => void;
    dismiss?: boolean;
  }>;
  /** Custom CSS class added to the modal window element (scopes per-modal overrides) */
  windowClass?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  constructor(private ngbModal: NgbModal) {}

  /**
   * Open a modal with the given options
   * @param options Modal options
   * @returns NgbModalRef for controlling the modal
   */
  open(options: ModalOptions): NgbModalRef {
    const modalOptions: any = {
      size: options.size,
      centered: options.centered,
      backdrop: options.backdrop,
      keyboard: options.keyboard
    };

    // Build windowClass: merge width class and any caller-supplied class
    const windowClasses: string[] = [];
    if (options.width) {
      windowClasses.push('custom-modal-width');
    }
    if (options.windowClass) {
      windowClasses.push(options.windowClass);
    }
    if (windowClasses.length > 0) {
      modalOptions.windowClass = windowClasses.join(' ');
    }

    const modalRef = this.ngbModal.open(ModalComponent, modalOptions);

    const component = modalRef.componentInstance as ModalComponent;

    // Set config
    component.config = {
      title: options.title || 'Modal',
      size: options.size || 'lg',
      centered: options.centered !== false,
      backdrop: options.backdrop !== false,
      keyboard: options.keyboard !== false,
      closeButton: options.closeButton !== false
    };

    // Set content
    // Use headerContent if provided, otherwise fall back to title
    if (options.headerContent) {
      component.headerContent = options.headerContent;
    } else if (options.title) {
      component.headerContent = options.title;
    }
    if (options.bodyContent) {
      component.bodyContent = options.bodyContent;
    }
    if (options.footerContent) {
      component.footerContent = options.footerContent;
    }
    if (options.footerButtons && options.footerButtons.length > 0) {
      component.footerButtons = options.footerButtons;
    }
    if (options.width) {
      component.width = options.width;
    }

    // Handle close
    component.closeModal.subscribe(() => {
      modalRef.dismiss();
    });

    return modalRef;
  }

  /**
   * Open a confirmation modal
   * @param title Modal title
   * @param message Confirmation message
   * @param onConfirm Callback for confirm action
   * @param onCancel Callback for cancel action
   */
  confirm(
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void
  ): NgbModalRef {
    return this.open({
      title,
      headerContent: title,
      bodyContent: message,
      centered: true,
      footerButtons: [
        {
          label: 'Cancel',
          cssClass: 'btn-light',
          action: onCancel,
          dismiss: true
        },
        {
          label: 'Confirm',
          cssClass: 'btn-primary',
          action: onConfirm,
          dismiss: true
        }
      ]
    });
  }

  /**
   * Open an alert modal
   * @param title Modal title
   * @param message Alert message
   * @param type Alert type (info, warning, danger, success)
   */
  alert(title: string, message: string, type: 'info' | 'warning' | 'danger' | 'success' = 'info'): NgbModalRef {
    return this.open({
      title,
      headerContent: title,
      bodyContent: message,
      centered: true,
      footerButtons: [
        {
          label: 'OK',
          cssClass: 'btn-primary',
          dismiss: true
        }
      ]
    });
  }

  /**
   * Open a loading modal (non-dismissible)
   * @param title Modal title
   * @param message Loading message
   */
  loading(title: string, message: string): NgbModalRef {
    return this.open({
      title,
      bodyContent: message,
      backdrop: 'static',
      keyboard: false,
      closeButton: false
    });
  }
}
